import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactWidget = ({ practitionerId, practitionerName, onClose }) => {
  const { user, userProfile } = useAuth();
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user || !userProfile || !message?.trim() || !subject?.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // First, create or get existing conversation
      let conversationId = null;
      
      // Check if conversation already exists
      const { data: existingConversation, error: conversationError } = await supabase
        ?.from('conversations')
        ?.select('id')
        ?.eq('client_id', user?.id)
        ?.eq('practitioner_id', practitionerId)
        ?.eq('subject', subject?.trim())
        ?.single();

      if (existingConversation) {
        conversationId = existingConversation?.id;
      } else {
        // Create new conversation
        const { data: newConversation, error: createError } = await supabase
          ?.from('conversations')
          ?.insert({
            client_id: user?.id,
            practitioner_id: practitionerId,
            subject: subject?.trim(),
            status: 'active'
          })
          ?.select('id')
          ?.single();

        if (createError) {
          throw new Error(createError?.message || 'Failed to create conversation');
        }
        
        conversationId = newConversation?.id;
      }

      if (!conversationId) {
        throw new Error('Failed to establish conversation');
      }

      // Send the message
      const { error: messageError } = await supabase
        ?.from('messages')
        ?.insert({
          conversation_id: conversationId,
          sender_id: user?.id,
          content: message?.trim(),
          status: 'sent'
        });

      if (messageError) {
        throw new Error(messageError?.message || 'Failed to send message');
      }

      setSuccess(true);
      setMessage('');
      setSubject('');
      
      // Auto-close after success
      setTimeout(() => {
        onClose?.();
      }, 2000);

    } catch (err) {
      console.error('Contact form error:', err);
      setError(err?.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Message Sent Successfully!
            </h3>
            <p className="text-muted-foreground mb-4">
              Your message has been sent to {practitionerName}. They will get back to you soon.
            </p>
            <Button onClick={onClose} variant="default" fullWidth>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            Contact {practitionerName}
          </h3>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e?.target?.value)}
              placeholder="What would you like to discuss?"
              required
              className="w-full"
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e?.target?.value)}
                placeholder="Type your message here..."
                required
                rows={5}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Be specific about your needs and any questions you have.
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!subject?.trim() || !message?.trim()}
              className="flex-1"
            >
              <Icon name="Send" size={16} className="mr-2" />
              Send Message
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactWidget;